import React, {useMemo, useState} from 'react';
import {CloseOutlined} from '@ant-design/icons';
import {Button, Checkbox, Input, Popover, Tag} from 'antd';
import {useTranslation} from 'react-i18next';
import type {ReasonOptionGroup, ReasonOptionLeaf} from '../hooks/usePopReviewOptions';

const {TextArea} = Input;

/** 审核备注长度上限，与 podReview 保持一致 */
const REMARK_MAX_LENGTH = 120;

interface ReasonCategory {
    label: string;
    options: ReasonOptionLeaf[];
}

interface PopReviewRejectPopoverProps {
    /** 浮窗开合由调用方受控，便于与合格确认浮窗显式互斥 */
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** 当前场景的不合格原因（已按一级分类分组） */
    reasonOptions: ReasonOptionGroup[];
    submitting: boolean;
    /** 提交不合格；返回 false 时浮窗保持打开，便于修正后重试 */
    onSubmit: (reasonCodes: string[], remark: string) => Promise<boolean>;
    /** 预填原因码（改判时回显当前结论）；调用方以 key 绑定当前单保证切单重置 */
    initialCodes?: string[];
    /** 预填备注原文（改判时回显） */
    initialRemark?: string;
    /** 改判态：标题与提交按钮切换为「修改」语义，并提示会覆盖原结论 */
    revising?: boolean;
    /** 触发按钮 */
    children: React.ReactNode;
}

const ROW_CLASS = 'flex items-center gap-2 px-2 py-2 rounded-lg my-0.5 cursor-pointer border-l-[3px]';
const CHECKBOX_CLASS =
    'flex-shrink-0 [&_.ant-checkbox-checked_.ant-checkbox-inner]:bg-ant-purple-6 [&_.ant-checkbox-checked_.ant-checkbox-inner]:border-ant-purple-6';

/**
 * 不合格原因浮窗，视觉对齐 POD 原因浮层：左栏一级分类（仅导航，显示已选数量），右栏该分类下的二级原因多选。
 * POP 必须选到二级：一级不可单独勾选，提交的始终是二级原因码。
 */
const PopReviewRejectPopover: React.FC<PopReviewRejectPopoverProps> = ({
    open,
    onOpenChange,
    reasonOptions,
    submitting,
    onSubmit,
    initialCodes,
    initialRemark,
    revising = false,
    children
}) => {
    const {t} = useTranslation();
    const [activeIndex, setActiveIndex] = useState(0);
    const [selectedCodes, setSelectedCodes] = useState<string[]>(initialCodes ?? []);
    const [remark, setRemark] = useState(initialRemark ?? '');

    // 未分类的原因（顶层叶子）归入同一个「其它」分类
    const categories = useMemo<ReasonCategory[]>(() => {
        const grouped: ReasonCategory[] = [];
        const ungrouped: ReasonOptionLeaf[] = [];
        reasonOptions.forEach(option => {
            if ('options' in option) {
                grouped.push({label: option.label, options: option.options});
            } else {
                ungrouped.push(option);
            }
        });
        if (ungrouped.length) {
            grouped.push({label: t('popReview.detail.uncategorizedReason'), options: ungrouped});
        }
        return grouped;
    }, [reasonOptions, t]);

    const activeCategory = categories[activeIndex];

    const toggle = (code: string) =>
        setSelectedCodes(prev => (prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]));

    const handleSubmit = async () => {
        const ok = await onSubmit(selectedCodes, remark);
        if (ok) {
            onOpenChange(false);
        }
    };

    const colHeader = (step: string, title: string, hint: string) => (
        <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-gray-100">
            <span className="flex-shrink-0 w-[18px] h-[18px] rounded-full bg-ant-purple-6 text-white text-[11px] leading-[18px] text-center">
                {step}
            </span>
            <span className="text-[13px] font-semibold text-gray-800">{title}</span>
            <span className="text-[11px] text-gray-400">{hint}</span>
        </div>
    );

    const selectedChips = categories.flatMap(category =>
        category.options
            .filter(option => selectedCodes.includes(option.value))
            .map(option => ({code: option.value, text: `${category.label} / ${option.label}`}))
    );

    const content = (
        <div className="flex flex-col w-[min(680px,56vw)] min-w-[600px] max-h-[70vh]">
            <div className="flex-shrink-0 px-5 py-3.5 border-b border-gray-100 flex items-center gap-2.5">
                <span className="flex-shrink-0 text-[15px] font-bold">
                    {revising
                        ? t('podReview.messages.disapproveModifyTitle')
                        : t('podReview.detail.selectReasonTitle')}
                </span>
                <span className="text-[11.5px] text-gray-500 min-w-0">{t('popReview.detail.selectReasonHint')}</span>
                {revising && (
                    <span className="flex-shrink-0 whitespace-nowrap text-[11.5px] text-orange-600 bg-orange-50 border border-orange-200 rounded-md px-2 py-1">
                        {t('podReview.detail.editingOverwriteHint')}
                    </span>
                )}
                <span className="ml-auto flex-shrink-0 text-[11.5px] text-ant-purple-7 bg-ant-purple-1 rounded-[10px] px-2 py-0.5">
                    {t('podReview.detail.selectedCount', {count: selectedCodes.length})}
                </span>
                <CloseOutlined
                    className="flex-shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer"
                    onClick={() => onOpenChange(false)}
                />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
                <div className="border border-ant-purple-2 rounded-[10px]">
                    <div className="grid grid-cols-[2fr_3fr] divide-x divide-gray-100">
                        <div className="flex flex-col min-w-0">
                            {colHeader(
                                '1',
                                t('podReview.detail.reasonLevel1'),
                                t('popReview.detail.reasonLevel1Hint')
                            )}
                            <div className="p-1.5 overflow-y-auto" style={{maxHeight: 260, minHeight: 200}}>
                                {categories.map((category, index) => {
                                    const count = category.options.filter(o =>
                                        selectedCodes.includes(o.value)
                                    ).length;
                                    const focused = index === activeIndex;
                                    return (
                                        <div
                                            key={category.label}
                                            onClick={() => setActiveIndex(index)}
                                            className={`${ROW_CLASS} ${
                                                focused
                                                    ? 'border-ant-purple-8 bg-ant-purple-1'
                                                    : count > 0
                                                      ? 'border-ant-purple-4 bg-ant-purple-1/40'
                                                      : 'border-transparent hover:bg-gray-50'
                                            }`}
                                        >
                                            <span className="text-[13px] truncate flex-1">{category.label}</span>
                                            {count > 0 && (
                                                <span className="flex-shrink-0 text-[11px] font-semibold text-ant-purple-7 bg-ant-purple-2 rounded-[9px] px-1.5">
                                                    {count}
                                                </span>
                                            )}
                                            <span className="text-gray-300 text-xs">›</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="flex flex-col min-w-0">
                            {colHeader(
                                '2',
                                t('podReview.detail.reasonLevel2'),
                                t('popReview.detail.reasonLevel2Hint')
                            )}
                            <div className="p-1.5 overflow-y-auto" style={{maxHeight: 260, minHeight: 200}}>
                                {activeCategory ? (
                                    activeCategory.options.map(option => {
                                        const checked = selectedCodes.includes(option.value);
                                        // 判定标准按原因码在前端 i18n 维护，未收录的码不显示
                                        const criteria = t(`popReview.reasonCriteria.${option.value}`, {
                                            defaultValue: ''
                                        });
                                        return (
                                            <div
                                                key={option.value}
                                                onClick={() => toggle(option.value)}
                                                className={`${ROW_CLASS} ${
                                                    checked
                                                        ? 'border-ant-purple-4 bg-ant-purple-1/40'
                                                        : 'border-transparent hover:bg-gray-50'
                                                }`}
                                            >
                                                <Checkbox
                                                    checked={checked}
                                                    onClick={e => e.stopPropagation()}
                                                    onChange={() => toggle(option.value)}
                                                    className={CHECKBOX_CLASS}
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-[13px]">{option.label}</div>
                                                    {criteria && (
                                                        <div className="text-[11.5px] text-gray-400 leading-5">
                                                            {criteria}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="text-[11.5px] text-gray-400 px-2 py-2">
                                        {t('popReview.detail.selectCategory')}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-100 px-2.5 py-2">
                        <div className="text-[11px] text-gray-400 mb-1.5">
                            {t('podReview.detail.selectedReasonPaths')}
                        </div>
                        {selectedChips.length === 0 ? (
                            <div className="text-[12px] text-gray-300">{t('podReview.detail.noReasonSelected')}</div>
                        ) : (
                            <div className="flex flex-wrap gap-1.5">
                                {selectedChips.map(chip => (
                                    <Tag
                                        key={chip.code}
                                        closable
                                        onClose={e => {
                                            e.preventDefault();
                                            toggle(chip.code);
                                        }}
                                        className="m-0 text-[11.5px] bg-ant-purple-1 border-ant-purple-3 text-ant-purple-9"
                                    >
                                        {chip.text}
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* 底部留白给 showCount 字数统计，避免被滚动容器裁切 */}
                <div className="mt-3 pb-5">
                    <div className="text-[11px] text-gray-400 mb-1">{t('popReview.detail.reviewRemark')}</div>
                    <TextArea
                        rows={2}
                        style={{resize: 'none'}}
                        maxLength={REMARK_MAX_LENGTH}
                        showCount
                        value={remark}
                        onChange={e => setRemark(e.target.value)}
                        placeholder={t('popReview.detail.remarkPlaceholder') as string}
                    />
                </div>
            </div>

            <div className="flex-shrink-0 px-4 py-3 border-t border-gray-100 flex items-center gap-2">
                <Button className="ml-auto" onClick={() => onOpenChange(false)}>
                    {t('common.cancel')}
                </Button>
                {/* 首次不合格用危险色强调；改判用主色紫，与 POD 一致 */}
                <Button
                    type="primary"
                    danger={!revising}
                    loading={submitting}
                    disabled={selectedCodes.length === 0}
                    onClick={() => void handleSubmit()}
                    className={
                        revising
                            ? 'bg-ant-purple-6 hover:!bg-ant-purple-7 border-ant-purple-6 hover:!border-ant-purple-7'
                            : ''
                    }
                >
                    {revising ? t('podReview.actions.saveChanges') : t('popReview.detail.submitReject')}
                </Button>
            </div>
        </div>
    );

    return (
        <Popover
            trigger="click"
            placement="bottomRight"
            arrow={false}
            open={open}
            onOpenChange={onOpenChange}
            content={content}
            styles={{body: {padding: 0, borderRadius: 12, overflow: 'hidden'}}}
        >
            {children}
        </Popover>
    );
};

export default PopReviewRejectPopover;
